import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  SelectChangeEvent,
  Slider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';
import SurveyContactData from '../../../../components/SurveyContactData/SurveyContactData';
import DesignChanger from '../../../../components/DesignChanger/DesignChanger';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import { ISurvey } from '../../../../shared/interfaces/ISurvey';
import PlatformSelect from '../../../../components/PlatformSelect/PlatformSelect';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import PlatformService from '../../../../services/PlatformService';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../providers/AuthProvider';
import { INpsEntry } from '../../../../shared/interfaces/INpsEntry';
import SurveyService from '../../../../services/SurveyService';
import { IContactData } from '../../../../shared/interfaces/IContactData';
import SurveyUtils from '../../../../shared/SurveyUtils';
import StringUtils from '../../../../shared/StringUtils';

const defaultDesign: IDesign = {
  companyLogo: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
  customFont: '',
  customFontFormat: '',
  customCss: '',
};

const defaultContactData = {
  privacyPolicyLink: '',
  contactEmail: '',
  imprintLink: '',
  logoLink: '',
};

const defaultNpsData: INpsEntry = {
  archived: false,
  archivedAt: '',
  averageRating: 0,
  companyName: '',
  companyWebsiteUrl: '',
  contactForm: false,
  createdAt: '',
  customFontFormat: '',
  customCss: '',
  id: '',
  label: '',
  lang: 'de',
  note: '',
  platform: '',
  question: 'Wie wahrscheinlich ist es, dass Sie uns weiterempfehlen?',
  questionEditable: false,
  score: 9,
  totalCount: 0,
  type: 'NPS',
  updatedAt: '',
  platformUrl: '',
  platformName: '',
  slug: '',
  title: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  introText:
    'Wir bedanken uns für Ihre Anregungen und Hinweise. Durch die Auswertung Ihrer Antworten helfen Sie uns dabei, unsere internen Abläufe zu optimieren sowie Schwachstellen zu identifizieren, sodass wir uns als Unternehmen verbessern können.',
  introTextNegative:
    'Wir bedanken uns für Ihre Anregungen und Hinweise. Durch die Auswertung Ihrer Antworten helfen Sie uns dabei, unsere internen Abläufe zu optimieren sowie Schwachstellen zu identifizieren, sodass wir uns als Unternehmen verbessern können.',
  outroText: 'Ihr Team von [Firmenname]',
  scale: {
    highText: 'Sehr wahrscheinlich',
    lowText: 'Sehr unwahrscheinlich',
  },
  feedbackActive: false,
  feedbackText: 'Feedback / konstruktive Kritik / Verbesserungsvorschläge',
  feedbackTitle: 'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)',
  i18n: {},
  shouldShowPrivacyNotice: false,
  privacyNoticeText: 'Ich stimme zu, dass meine Daten zu Analysezwecken gespeichert werden.',
  privacyNoticeLink: 'https://example.com/privacy',
};

interface Props {
  id?: string;
  loadSurveyType?: string;
  isEdit?: boolean;
  reload: () => Promise<void>;
  hasAnswers: boolean;
}

const NetPromoterScoreGuide: FC<Props> = ({ id, isEdit, reload, loadSurveyType, hasAnswers }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [npsScoreSlider, setNpsScoreSlider] = useState<number>(defaultNpsData.score);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState(0);
  const [canEditDesign, setCanEditDesign] = useState(false);
  const [showContactData, setShowContactData] = useState(defaultNpsData.contactForm);
  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [npsData, setNpsData] = useState<INpsEntry>(defaultNpsData);
  const [showUrlError, setShowUrlError] = useState<boolean>(false);
  const [contactData, setContactData] = useState<IContactData>(defaultContactData);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(npsData.feedbackActive);
  const [surveyID, setSurveyID] = useState<string>(id || '');

  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const getMergedState = (designOverride: IDesign = design): ISurvey => ({
    ...npsData,
    ...contactData,
    ...designOverride,
  });
  const handleAddSurvey = async () => {
    const mergedState: ISurvey = getMergedState();
    if (isEdit) {
      if (!id) return;
      await SurveyService.updateNpsSurvey(id, mergedState);
    } else {
      mergedState.slug = '';
      await SurveyService.addNpsSurvey(mergedState);
    }
    await reload();
  };

  const handleOutroTextChange = (companyName: string) => {
    const value = npsData.outroText.replace(/\[Firmenname\]/, companyName || '[Firmenname]');
    setNpsData((prevState) => ({
      ...prevState,
      outroText: value,
    }));
  };

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async (signal: AbortSignal) => {
      const platforms = await PlatformService.getPlatforms(setLoading, signal);
      if (!platforms) return;

      setPlatforms([...platforms, PlatformService.getOthersPseudoPlatform(t)]);
    };

    (async () => await getPlatforms(controller.signal))();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!hasRole('can-use-survey')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    if (!surveyID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurvey = async () => {
      setLoading(true);
      try {
        const endpointPath = SurveyUtils.getEndpointByType(loadSurveyType);
        const survey = await SurveyService.getSurveyPublic(
          surveyID,
          endpointPath,
          setLoading,
          signal
        );
        if (!survey) {
          setLoading(false);
          return;
        }
        if (survey.type === 'NPS') {
          setNpsData(survey);
        }
        setDesign({
          companyLogo: survey.companyLogo,
          primaryColor: survey.primaryColor,
          secondaryColor: survey.secondaryColor,
          customFont: survey.customFont,
          customFontFormat: survey.customFontFormat,
          customCss: survey.customCss ?? '',
        });
        setContactData({
          privacyPolicyLink: survey.privacyPolicyLink,
          contactEmail: survey.contactEmail,
          imprintLink: survey.imprintLink,
          logoLink: survey.logoLink,
        });
        setIsCheckboxChecked(survey.feedbackActive);
        setShowContactData(
          survey.imprintLink !== '' || survey.contactEmail !== '' || survey.privacyPolicyLink !== ''
        );
        setCanEditDesign(
          survey.primaryColor !== '#357E8B' ||
            survey.secondaryColor !== '#000000' ||
            survey.companyLogo !== '' ||
            !!survey.customCss
        );
      } catch (error) {
        console.error('Failed to fetch survey:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurvey();

    return () => {
      controller.abort();
    };
  }, [surveyID]);

  const shouldRender = (stepId: string) => {
    const ALWAYS_EDITABLE = ['survey_label', 'comment', 'title', 'intro_outro'];
    const RENDER_WHEN_FRESH: string[] = ['question_and_score', 'scale_label', 'portal_data'];

    if (!isEdit) {
      return true;
    }

    if (ALWAYS_EDITABLE.includes(stepId)) {
      return true;
    }

    if (!hasAnswers && RENDER_WHEN_FRESH.includes(stepId)) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Box sx={{ maxWidth: '600px' }}>
        <Stepper
          activeStep={currentSurveyGuideStep}
          orientation="vertical"
        >
          {shouldRender('survey_label') && (
            <Step>
              <StepLabel>{t('survey_label')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('survey_name')}
                  placeholder="z.B.: Musterfirma"
                  variant="outlined"
                  size="small"
                  value={npsData.label}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setNpsData((prevState) => ({
                      ...prevState,
                      label: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: 400 }}
                  autoFocus
                />
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Button
                    disabled={npsData.label === ''}
                    variant="contained"
                    onClick={handleNextSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('comment') && (
            <Step>
              <StepLabel>{t('question_and_score')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('nps_survey')}
                  size={'small'}
                  multiline
                  maxRows={'2'}
                  variant={'outlined'}
                  value={npsData.question}
                  placeholder={t('nps_survey')}
                  disabled={!shouldRender('question_and_score')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setNpsData((prevState) => ({
                      ...prevState,
                      question: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
                  <Typography>{t('nps_score')}</Typography>
                  <Slider
                    marks
                    valueLabelDisplay={'auto'}
                    value={npsData.score ? npsData.score : npsScoreSlider}
                    min={0}
                    max={10}
                    size={'small'}
                    disabled={!shouldRender('question_and_score')}
                    onChange={(event: Event, value: number | number[]) => {
                      setNpsScoreSlider(value as number);
                      setNpsData((prevState) => ({
                        ...prevState,
                        score: value as number,
                      }));
                    }}
                  />
                </Box>
                <Typography>
                  {t('enable_comment_field')}{' '}
                  <Checkbox
                    checked={isCheckboxChecked}
                    onChange={() => {
                      setIsCheckboxChecked((prevState) => !prevState);
                      setNpsData((prevState) => ({
                        ...prevState,
                        feedbackActive: !prevState.feedbackActive,
                      }));
                    }}
                  />
                </Typography>
                <Box sx={{ mb: theme.spacing(2), mt: theme.spacing(1) }}>
                  <Button
                    disabled={npsData.question === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {isCheckboxChecked && shouldRender('comment') && (
            <Step>
              <StepLabel>{`${t('comment')} (${t('optional_configuration')})`} </StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('comment_field_title')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    value={npsData.feedbackTitle}
                    placeholder={t('comment_field_title_placeholder')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        feedbackTitle: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={t('comment_field')}
                    size={'medium'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={npsData.feedbackText}
                    placeholder={t('comment_field_placeholder')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        feedbackText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <Typography>
                    {t('show_privacy_notice')}{' '}
                    <Checkbox
                      checked={npsData.shouldShowPrivacyNotice}
                      onChange={() => {
                        setNpsData((prevState) => ({
                          ...prevState,
                          shouldShowPrivacyNotice: !prevState.shouldShowPrivacyNotice,
                          privacyNoticeText: prevState.privacyNoticeText
                            ? prevState.privacyNoticeText
                            : defaultNpsData.privacyNoticeText,
                        }));
                      }}
                    />
                  </Typography>

                  {npsData.shouldShowPrivacyNotice && (
                    <>
                      <TextField
                        label={t('survey_privacy_notice')}
                        size={'medium'}
                        multiline
                        maxRows={'3'}
                        variant={'outlined'}
                        value={npsData.privacyNoticeText}
                        placeholder={t('survey_privacy_notice_placeholder')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setNpsData((prevState) => ({
                            ...prevState,
                            privacyNoticeText: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                      <TextField
                        label={t('survey_privacy_notice_link')}
                        size={'medium'}
                        maxRows={'3'}
                        variant={'outlined'}
                        required={true}
                        value={npsData.privacyNoticeLink}
                        placeholder={t('survey_privacy_notice_link_placeholder')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setNpsData((prevState) => ({
                            ...prevState,
                            privacyNoticeLink: event.target.value,
                          }));
                        }}
                        sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                      />
                    </>
                  )}
                </Box>
                <Box>
                  <Button
                    disabled={
                      npsData.shouldShowPrivacyNotice &&
                      (!npsData.privacyNoticeLink || npsData.privacyNoticeLink === '')
                    }
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('scale_label') && (
            <Step>
              <StepLabel>{t('scale_label')}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('scale0_label')}
                    size="small"
                    variant="outlined"
                    placeholder={t('scale0_placeholder')}
                    value={npsData.scale.lowText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        scale: {
                          ...prevState.scale,
                          lowText: event.target.value,
                        },
                      }));
                    }}
                    fullWidth
                    sx={{ mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={t('scale10_label')}
                    size="small"
                    variant="outlined"
                    placeholder={t('scale10_placeholder')}
                    value={npsData.scale.highText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        scale: {
                          ...prevState.scale,
                          highText: event.target.value,
                        },
                      }));
                    }}
                    fullWidth
                    sx={{ mt: theme.spacing(2) }}
                  />
                </Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('company_data_label') && (
            <Step>
              <StepLabel>{t('company_data_label')}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('company_name')}
                    size="small"
                    variant="outlined"
                    placeholder={t('company_name_placeholder_nps')}
                    value={npsData.companyName}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        companyName: event.target.value,
                      }));
                    }}
                    fullWidth
                    sx={{ mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={t('enter_company_url')}
                    size="small"
                    variant="outlined"
                    placeholder={t('company_url_placeholder_nps')}
                    value={npsData.companyWebsiteUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        companyWebsiteUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                    fullWidth
                  />
                </Box>
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    disabled={!npsData.companyName || !npsData.companyWebsiteUrl || showUrlError}
                    variant="contained"
                    onClick={() => {
                      if (!StringUtils.isValidCompanyUrl(npsData.companyWebsiteUrl)) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                      handleOutroTextChange(npsData.companyName);
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('title') && (
            <Step>
              <StepLabel>{t('title')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('title')}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={npsData.title}
                  placeholder={t('title_placeholder')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setNpsData((prevState) => ({
                      ...prevState,
                      title: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('intro_outro') && (
            <Step>
              <StepLabel>{t('outro')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('outro')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={npsData.introText}
                  placeholder={`${npsData.introText} ${npsData.companyName}`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setNpsData((prevState) => ({
                      ...prevState,
                      introText: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={t('outro_negative_nps')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={npsData.introTextNegative}
                  placeholder={`${npsData.introTextNegative} ${npsData.companyName}`}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setNpsData((prevState) => ({
                      ...prevState,
                      introTextNegative: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={t('signature')}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={npsData.outroText}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setNpsData((prevState) => ({
                      ...prevState,
                      outroText: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    //disabled={NpsData.question === ''}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('portal_data') && (
            <Step>
              <StepLabel>{`${t('portal_data')} (${t('optional_configuration')})`}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <PlatformSelect
                    value={npsData.platform}
                    onChange={(event: SelectChangeEvent) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        platform: event.target.value,
                      }));
                    }}
                    platforms={platforms}
                  />
                  <TextField
                    label={`${t('portal_data')}-URL`}
                    size="small"
                    variant="outlined"
                    placeholder={t('portal_data_url_placeholder')}
                    value={npsData.platformUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setNpsData((prevState) => ({
                        ...prevState,
                        platformUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (
                        !StringUtils.isValidPlatformUrl(npsData.platformUrl) &&
                        npsData.platformUrl !== ''
                      ) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          <Step>
            <StepLabel>{t('finish_configuration')}</StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  onClick={handleAddSurvey}
                >
                  {t('finish')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handlePreviousSurveyGuideStep}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                >
                  {t('back')}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Box>
      {!isEdit ? (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={npsData.lang}
                    exclusive
                    onChange={(event: MouseEvent, value: string | null) => {
                      if (value) {
                        setNpsData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('design')}</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="NPS"
                    buildPreviewSurvey={getMergedState}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                logoLink={contactData.logoLink}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={npsData.lang}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string | null) => {
                      if (value) {
                        setNpsData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('design')}</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="NPS"
                    buildPreviewSurvey={getMergedState}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                logoLink={contactData.logoLink}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default NetPromoterScoreGuide;
